(() => {
  const CUSTOM_ELEMENT_NAME = 'ag-bundle-product';
  const SLIDER_SELECTOR = '[data-ag-bundle-component-slider]';
  const PREVIOUS_SELECTOR = '[data-ag-bundle-component-previous]';
  const NEXT_SELECTOR = '[data-ag-bundle-component-next]';
  const GALLERY_TRIGGER_SELECTOR = '[data-ag-bundle-gallery-trigger]';
  const GALLERY_DIALOG_SELECTOR = '[data-ag-bundle-gallery-dialog]';
  const GALLERY_STAGE_SELECTOR = '.ag-main-bundle-product__gallery-stage';
  const GALLERY_SLIDER_SELECTOR = '[data-ag-bundle-gallery-modal-slider]';
  const GALLERY_CLOSE_SELECTOR = '[data-ag-bundle-gallery-close]';
  const GALLERY_PREVIOUS_SELECTOR = '[data-ag-bundle-gallery-previous]';
  const GALLERY_NEXT_SELECTOR = '[data-ag-bundle-gallery-next]';
  const MOBILE_MEDIA_QUERY = '(max-width: 767px)';
  const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
  const SLIDE_SPEED = 420;
  const ARROW_FEEDBACK_DURATION = 600;

  class AgBundleProduct extends HTMLElement {
    constructor() {
      super();
      this.isInitialized = false;
      this.mobileMedia = window.matchMedia(MOBILE_MEDIA_QUERY);
      this.reducedMotion = window.matchMedia(REDUCED_MOTION_QUERY);
      this.sliderRecords = [];
      this.arrowFeedbackTimers = new Map();
      this.navigationFocusFrame = null;
      this.galleryId = '';
      this.galleryDialog = null;
      this.galleryStage = null;
      this.gallerySlider = null;
      this.galleryClose = null;
      this.gallerySwiper = null;
      this.activeGalleryTrigger = null;
      this.addedOverflowHidden = false;

      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleMotionChange = this.handleMotionChange.bind(this);
      this.handleWindowLoad = this.handleWindowLoad.bind(this);
      this.handleGalleryTriggerClick = this.handleGalleryTriggerClick.bind(this);
      this.handleGalleryDialogClick = this.handleGalleryDialogClick.bind(this);
      this.handleGalleryCloseClick = this.handleGalleryCloseClick.bind(this);
      this.handleGalleryClosed = this.handleGalleryClosed.bind(this);
      this.handleGalleryButtonClick = this.handleGalleryButtonClick.bind(this);
    }

    connectedCallback() {
      if (this.isInitialized) return;

      this.isInitialized = true;
      this.sliderRecords = Array.from(this.querySelectorAll(SLIDER_SELECTOR)).map((element) => ({
        element,
        instance: null,
      }));
      this.galleryId = this.dataset.agBundleGalleryId || '';
      this.galleryDialog = this.querySelector(GALLERY_DIALOG_SELECTOR);
      this.galleryStage = this.querySelector(GALLERY_STAGE_SELECTOR);
      this.gallerySlider = this.querySelector(GALLERY_SLIDER_SELECTOR);
      this.galleryClose = this.querySelector(GALLERY_CLOSE_SELECTOR);

      this.mobileMedia.addEventListener('change', this.handleMediaChange);
      this.reducedMotion.addEventListener('change', this.handleMotionChange);
      this.addEventListener('click', this.handleGalleryButtonClick, true);
      window.addEventListener('load', this.handleWindowLoad);
      document.addEventListener('click', this.handleGalleryTriggerClick);
      this.galleryDialog?.addEventListener('click', this.handleGalleryDialogClick);
      this.galleryDialog?.addEventListener('close', this.handleGalleryClosed);
      this.galleryClose?.addEventListener('click', this.handleGalleryCloseClick);

      this.syncSliders();
    }

    disconnectedCallback() {
      if (!this.isInitialized) return;

      this.mobileMedia.removeEventListener('change', this.handleMediaChange);
      this.reducedMotion.removeEventListener('change', this.handleMotionChange);
      this.removeEventListener('click', this.handleGalleryButtonClick, true);
      this.arrowFeedbackTimers.forEach((timer, button) => {
        window.clearTimeout(timer);
        button.classList.remove('is-pressed');
      });
      this.arrowFeedbackTimers.clear();
      window.cancelAnimationFrame(this.navigationFocusFrame);
      window.removeEventListener('load', this.handleWindowLoad);
      document.removeEventListener('click', this.handleGalleryTriggerClick);
      this.galleryDialog?.removeEventListener('click', this.handleGalleryDialogClick);
      this.galleryDialog?.removeEventListener('close', this.handleGalleryClosed);
      this.galleryClose?.removeEventListener('click', this.handleGalleryCloseClick);
      this.destroySliders();
      this.destroyGallerySlider();
      window.cancelAnimationFrame(this.galleryFrame);
      if (this.galleryDialog?.open) this.galleryDialog.close();
      this.unlockPageScroll();
      this.sliderRecords = [];
      this.galleryId = '';
      this.galleryDialog = null;
      this.galleryStage = null;
      this.gallerySlider = null;
      this.galleryClose = null;
      this.activeGalleryTrigger = null;
      this.isInitialized = false;
    }

    handleMotionChange() {
      const speed = this.reducedMotion.matches ? 0 : SLIDE_SPEED;
      if (this.gallerySwiper) this.gallerySwiper.params.speed = speed;
      this.sliderRecords.forEach(({ instance }) => { if (instance) instance.params.speed = speed; });
    }

    handleMediaChange() {
      this.syncSliders();
    }

    handleWindowLoad() {
      this.syncSliders();
    }

    handleGalleryButtonClick(event) {
      const button = event.target.closest?.(
        `${PREVIOUS_SELECTOR}, ${NEXT_SELECTOR}, ${GALLERY_PREVIOUS_SELECTOR}, ${GALLERY_NEXT_SELECTOR}, ${GALLERY_CLOSE_SELECTOR}`
      );
      if (!this.mobileMedia.matches || !button || button.disabled || button.classList.contains('swiper-button-disabled')) return;

      const slider = button.closest(SLIDER_SELECTOR);
      if (slider && document.activeElement === button && button.matches(':focus-visible')) {
        const oppositeButton = slider.querySelector(button.matches(NEXT_SELECTOR) ? PREVIOUS_SELECTOR : NEXT_SELECTOR);
        window.cancelAnimationFrame(this.navigationFocusFrame);
        this.navigationFocusFrame = window.requestAnimationFrame(() => {
          this.navigationFocusFrame = null;
          if (!this.isInitialized || !this.mobileMedia.matches || !button.disabled) return;
          if (document.activeElement !== button && document.activeElement !== document.body) return;
          if (oppositeButton && !oppositeButton.disabled) oppositeButton.focus({ preventScroll: true });
        });
      }

      // Capture the enabled state before Swiper disables navigation at the last slide.
      window.clearTimeout(this.arrowFeedbackTimers.get(button));
      button.classList.add('is-pressed');
      this.arrowFeedbackTimers.set(button, window.setTimeout(() => {
        button.classList.remove('is-pressed');
        this.arrowFeedbackTimers.delete(button);
      }, ARROW_FEEDBACK_DURATION));
    }

    handleGalleryTriggerClick(event) {
      const trigger = event.target.closest?.(GALLERY_TRIGGER_SELECTOR);
      if (!trigger || trigger.dataset.agBundleGalleryId !== this.galleryId) return;

      event.preventDefault();
      this.openGallery(trigger);
    }

    handleGalleryDialogClick(event) {
      if (event.target !== this.galleryDialog && event.target !== this.galleryStage) return;
      this.galleryDialog?.close();
    }

    handleGalleryCloseClick() {
      this.galleryDialog?.close();
    }

    handleGalleryClosed() {
      this.unlockPageScroll();
      if (this.activeGalleryTrigger?.isConnected) this.activeGalleryTrigger.focus();
      this.activeGalleryTrigger = null;
    }

    openGallery(trigger) {
      if (!this.galleryDialog || !this.gallerySlider) return;

      const requestedIndex = Number.parseInt(trigger.dataset.agBundleGalleryIndex || '0', 10);
      const slideIndex = Number.isNaN(requestedIndex) ? 0 : requestedIndex;
      this.activeGalleryTrigger = trigger;

      if (!this.galleryDialog.open) {
        this.galleryDialog.showModal();
        this.lockPageScroll();
      }

      const gallerySwiper = this.ensureGallerySlider();
      window.cancelAnimationFrame(this.galleryFrame);
      this.galleryFrame = window.requestAnimationFrame(() => {
        if (!this.isConnected || !this.galleryDialog?.open) return;
        gallerySwiper?.update();
        if (gallerySwiper?.params.loop && typeof gallerySwiper.slideToLoop === 'function') {
          gallerySwiper.slideToLoop(slideIndex, 0);
        } else {
          gallerySwiper?.slideTo(slideIndex, 0);
        }
        this.galleryClose?.focus({ preventScroll: true });
      });
    }

    ensureGallerySlider() {
      const current = this.gallerySwiper || this.gallerySlider?.swiper;
      if (current && !current.destroyed) {
        this.gallerySwiper = current;
        return current;
      }
      if (!this.gallerySlider || typeof window.Swiper === 'undefined') return null;

      const hasMultipleSlides = this.gallerySlider.querySelectorAll('.swiper-slide').length > 1;
      this.gallerySwiper = new window.Swiper(this.gallerySlider, {
        slidesPerView: 1,
        loop: hasMultipleSlides,
        speed: this.reducedMotion.matches ? 0 : SLIDE_SPEED,
        watchOverflow: true,
        navigation: {
          prevEl: this.querySelector(GALLERY_PREVIOUS_SELECTOR),
          nextEl: this.querySelector(GALLERY_NEXT_SELECTOR),
        },
      });
      return this.gallerySwiper;
    }

    destroyGallerySlider() {
      const current = this.gallerySwiper || this.gallerySlider?.swiper;
      if (current && !current.destroyed) current.destroy(true, true);
      this.gallerySwiper = null;
    }

    lockPageScroll() {
      this.addedOverflowHidden = !document.body.classList.contains('overflow-hidden');
      const rightGutterWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      document.documentElement.style.setProperty('--ag-bundle-gallery-right-gutter-width', `${rightGutterWidth}px`);
      document.body.classList.add('overflow-hidden');
    }

    unlockPageScroll() {
      if (this.addedOverflowHidden) document.body.classList.remove('overflow-hidden');
      document.documentElement.style.removeProperty('--ag-bundle-gallery-right-gutter-width');
      this.addedOverflowHidden = false;
    }

    syncSliders() {
      if (!this.isInitialized) return;

      if (!this.mobileMedia.matches) {
        this.destroySliders();
        return;
      }

      if (typeof window.Swiper === 'undefined') return;

      this.sliderRecords.forEach((record) => {
        const currentInstance = record.instance || record.element.swiper;
        if (currentInstance && !currentInstance.destroyed) {
          record.instance = currentInstance;
          return;
        }

        const previousButton = record.element.querySelector(PREVIOUS_SELECTOR);
        const nextButton = record.element.querySelector(NEXT_SELECTOR);
        const pagination = record.element.querySelector('[data-ag-bundle-component-pagination]');
        // Slides are product links; keep their native role and product name.
        const a11y = { slideRole: '', slideLabelMessage: '' };
        if (previousButton?.getAttribute('aria-label')) a11y.prevSlideMessage = previousButton.getAttribute('aria-label');
        if (nextButton?.getAttribute('aria-label')) a11y.nextSlideMessage = nextButton.getAttribute('aria-label');
        if (pagination?.dataset.slideLabel) a11y.paginationBulletMessage = pagination.dataset.slideLabel.replace('[index]', '{{index}}');

        record.instance = new window.Swiper(record.element, {
          slidesPerView: 1,
          speed: this.reducedMotion.matches ? 0 : SLIDE_SPEED,
          watchOverflow: true,
          a11y,
          navigation: {
            prevEl: previousButton,
            nextEl: nextButton,
          },
          pagination: {
            el: pagination,
            clickable: true,
          },
        });
      });
    }

    destroySliders() {
      this.sliderRecords.forEach((record) => {
        const currentInstance = record.instance || record.element.swiper;
        if (currentInstance && !currentInstance.destroyed) currentInstance.destroy(true, true);
        record.instance = null;
      });
    }
  }

  if (!customElements.get(CUSTOM_ELEMENT_NAME)) {
    customElements.define(CUSTOM_ELEMENT_NAME, AgBundleProduct);
  }
})();
